import React, { useCallback, useEffect, useMemo, useState } from "react";
import ApiURL from "../controllers/Api";
import { toast } from "../utils/toast";
import CompanyLayout from "./CompanyLayout";

const statuses = [
  "New",
  "Screening",
  "Interview Scheduled",
  "Interview Completed",
  "Shortlisted",
  "Offer Released",
  "Hired",
  "Closed",
];

const statusClasses = {
  New: "bg-slate-100 text-slate-700",
  Screening: "bg-amber-100 text-amber-700",
  "Interview Scheduled": "bg-sky-100 text-sky-700",
  "Interview Completed": "bg-cyan-100 text-cyan-700",
  Shortlisted: "bg-violet-100 text-violet-700",
  "Offer Released": "bg-indigo-100 text-indigo-700",
  Hired: "bg-emerald-100 text-emerald-700",
  Closed: "bg-rose-100 text-rose-700",
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString();
};

export default function ClientCompanyDashboard() {
  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);
  const isSubCompanyUser = currentUser?.user_type === "sub_company" || currentUser?.role === "sub_company";
  const [subCompanies, setSubCompanies] = useState([]);
  const [selectedSubCompanyId, setSelectedSubCompanyId] = useState("");
  const [summary, setSummary] = useState({
    total: 0,
    coming_for_interview: 0,
    interview_left: 0,
    interview_completed: 0,
    shortlisted: 0,
    hired: 0,
    closed: 0,
  });
  const [roles, setRoles] = useState([]);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);

  const loadSubCompanies = useCallback(async () => {
    const token = localStorage.getItem("token");
    const response = await fetch(`${ApiURL}crm/company/get_sub_companies.php`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || result.success === false) {
      throw new Error(result.message || "Failed to load client companies");
    }
    setSubCompanies(result?.data?.sub_companies || []);
  }, []);

  const loadDashboard = useCallback(async (subCompanyId = selectedSubCompanyId) => {
    const token = localStorage.getItem("token");
    const query = subCompanyId ? `?sub_company_id=${subCompanyId}` : "";
    const response = await fetch(`${ApiURL}crm/company/get_client_dashboard.php${query}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || result.success === false) {
      throw new Error(result.message || "Failed to load client dashboard");
    }
    setSummary(result?.data?.summary || {});
    setRoles(result?.data?.roles || []);
    setRows(result?.data?.rows || []);
  }, [selectedSubCompanyId]);

  useEffect(() => {
    const loadAll = async () => {
      try {
        await Promise.all([loadSubCompanies(), loadDashboard("")]);
      } catch (error) {
        console.error("Error loading client dashboard:", error);
        toast.error(error.message || "Failed to load client dashboard");
      }
    };

    loadAll();
  }, [loadDashboard, loadSubCompanies]);

  const handleClientChange = async (event) => {
    const nextId = event.target.value;
    setSelectedSubCompanyId(nextId);
    try {
      await loadDashboard(nextId);
    } catch (error) {
      console.error("Error filtering client dashboard:", error);
      toast.error(error.message || "Failed to filter client dashboard");
    }
  };

  const updateStatus = async (row, nextStatus, remarks) => {
    setSavingId(row.id);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${ApiURL}crm/company/update_client_assignment_status.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          assignment_id: row.id,
          status: nextStatus,
          remarks,
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.success === false) {
        throw new Error(result.message || "Failed to update status");
      }
      toast.success("Client status updated successfully.");
      await loadDashboard(selectedSubCompanyId);
      return true;
    } catch (error) {
      console.error("Error updating client status:", error);
      toast.error(error.message || "Failed to update status");
      return false;
    } finally {
      setSavingId(null);
    }
  };

  const openStatusModal = (row, nextStatus) => {
    if (!row || row.is_locked || nextStatus === row.lead_status) {
      return;
    }

    setPendingStatusChange({
      row,
      nextStatus,
      remarks: row.remarks || "",
    });
  };

  const closeStatusModal = () => {
    setPendingStatusChange(null);
  };

  const confirmStatusUpdate = async () => {
    if (!pendingStatusChange) {
      return;
    }

    const remarks = pendingStatusChange.remarks.trim();
    if (!remarks) {
      toast.warning("Remarks are mandatory before updating status.");
      return;
    }

    const success = await updateStatus(pendingStatusChange.row, pendingStatusChange.nextStatus, remarks);
    if (success) {
      closeStatusModal();
    }
  };

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;

    return rows.filter((row) =>
      [
        row.candidate_name,
        row.candidate_role,
        row.original_filename,
        row.recruiter_name,
        row.sub_company_name,
        row.requirement_name,
        row.lead_status,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    );
  }, [rows, search]);

  const cards = [
    { label: "Total Profiles", value: summary.total || 0, helper: "Candidates shared with clients" },
    { label: "Coming For Interview", value: summary.coming_for_interview || 0, helper: "Scheduled interview rows" },
    { label: "Interview Left", value: summary.interview_left || 0, helper: "Scheduled but not completed" },
    { label: "Interview Done", value: summary.interview_completed || 0, helper: "Completed interview rows" },
    { label: "Shortlisted", value: summary.shortlisted || 0, helper: "Selected for next step" },
    { label: "Hired", value: summary.hired || 0, helper: "Closed as hired" },
  ];

  return (
    <CompanyLayout
      title={isSubCompanyUser ? "Sub Company Dashboard" : "Client Company Dashboard"}
      description={
        isSubCompanyUser
          ? "Review candidates, role-wise movement, and interview status shared with your company."
          : "Share a clean client-facing view of interview counts, pending interviews, role-wise movement, and candidate status."
      }
    >
      <div className="space-y-4">
        {isSubCompanyUser ? (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 shadow-sm">
            <div className="text-sm font-semibold text-emerald-900">
              Logged in as {currentUser?.sub_company_name || currentUser?.company_name || currentUser?.first_name || "Sub Company"}
            </div>
            <div className="mt-1 text-xs text-emerald-700">
              Showing only resumes and requirements tagged to this sub-company.
            </div>
          </div>
        ) : (
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="grid gap-3 md:grid-cols-[minmax(220px,320px)_1fr] md:items-end">
            <label className="block">
              <span className="text-sm font-medium text-slate-700">Client Company</span>
              <select
                value={selectedSubCompanyId}
                onChange={handleClientChange}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="">All client companies</option>
                {subCompanies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </label>
            <div className="text-sm text-slate-500">
              Choose one client to review only their roles and interview pipeline, or keep all clients selected for a full company view.
            </div>
          </div>
        </div>
        )}

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-6">
          {cards.map((card) => (
            <div key={card.label} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <div className="text-xs font-medium text-slate-500">{card.label}</div>
              <div className="mt-2 text-3xl font-bold leading-none text-slate-900">{card.value}</div>
              <div className="mt-2 text-xs leading-5 text-slate-500">{card.helper}</div>
            </div>
          ))}
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Role Wise Interview Summary</h3>
              <p className="mt-1 text-sm text-slate-500">Each role shows total profiles, interviews coming, interviews left, and final outcomes.</p>
            </div>
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="border-b border-gray-200 bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Client</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Role</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Total</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Coming</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Left</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Done</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Shortlisted</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Hired</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Closed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {roles.map((role) => (
                  <tr key={`${role.sub_company_id}-${role.requirement_id}-${role.role}`} className="hover:bg-gray-50">
                    <td className="px-3 py-3 font-medium text-slate-900">{role.sub_company_name}</td>
                    <td className="px-3 py-3 text-slate-700">{role.role}</td>
                    <td className="px-3 py-3 text-slate-700">{role.total}</td>
                    <td className="px-3 py-3 text-sky-700">{role.coming_for_interview}</td>
                    <td className="px-3 py-3 text-amber-700">{role.interview_left}</td>
                    <td className="px-3 py-3 text-cyan-700">{role.interview_completed}</td>
                    <td className="px-3 py-3 text-violet-700">{role.shortlisted}</td>
                    <td className="px-3 py-3 text-emerald-700">{role.hired}</td>
                    <td className="px-3 py-3 text-rose-700">{role.closed}</td>
                  </tr>
                ))}
                {roles.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-3 py-8 text-center text-slate-500">
                      No client role data found yet. Tag resumes to client requirements and assign them to recruiters first.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="flex flex-col gap-4 border-b border-gray-200 px-4 py-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Candidate Interview Status</h3>
              <p className="mt-1 text-sm text-slate-500">Update the client-facing status as candidates move through interview rounds.</p>
            </div>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search candidate, role, client..."
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm md:max-w-xs"
            />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="border-b border-gray-200 bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Candidate</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Client / Role</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Recruiter</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Current Status</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Update Status</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Updated</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredRows.map((row) => (
                  <tr key={row.id} className="align-top hover:bg-gray-50">
                    <td className="px-3 py-3">
                      <div className="font-medium text-slate-900">{row.candidate_name || "Candidate"}</div>
                      <div className="mt-1 text-[11px] text-slate-500">{row.candidate_role || row.original_filename || "-"}</div>
                      <div className="mt-1 text-[11px] text-slate-500">ATS {row.ats_score || 0}%</div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="font-medium text-slate-900">{row.sub_company_name || "-"}</div>
                      <div className="mt-1 text-[11px] text-slate-500">{row.requirement_name || "-"}</div>
                    </td>
                    <td className="px-3 py-3 text-slate-600">{row.recruiter_name || "-"}</td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${statusClasses[row.lead_status] || "bg-slate-100 text-slate-700"}`}>
                        {row.lead_status}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <select
                        value={row.lead_status}
                        disabled={savingId === row.id || row.is_locked}
                        onChange={(event) => openStatusModal(row, event.target.value)}
                        className="min-w-[170px] rounded-md border border-gray-300 px-3 py-2 text-xs disabled:cursor-not-allowed disabled:bg-gray-100"
                        title={row.is_locked ? "This row is locked after hiring" : "Choose the next status"}
                      >
                        {statuses.map((status) => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                      {savingId === row.id ? <div className="mt-1 text-[11px] text-slate-500">Saving...</div> : null}
                    </td>
                    <td className="px-3 py-3 text-slate-600">{formatDate(row.updated_at || row.assigned_at)}</td>
                  </tr>
                ))}
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-8 text-center text-slate-500">
                      No candidates found for this client dashboard.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>

        {pendingStatusChange ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-lg rounded-2xl border border-gray-200 bg-white shadow-2xl">
              <div className="border-b border-gray-200 px-5 py-4">
                <h3 className="text-lg font-semibold text-slate-900">Update Client Status</h3>
                <p className="mt-1 text-sm text-slate-500">
                  {pendingStatusChange.row.candidate_name || "Candidate"}: {pendingStatusChange.row.lead_status} to {pendingStatusChange.nextStatus}
                </p>
              </div>

              <div className="space-y-4 px-5 py-4">
                <div className="rounded-xl border border-gray-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                  <div><span className="font-medium text-slate-900">Client:</span> {pendingStatusChange.row.sub_company_name || "-"}</div>
                  <div className="mt-1"><span className="font-medium text-slate-900">Requirement:</span> {pendingStatusChange.row.requirement_name || "-"}</div>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">
                    Remarks <span className="text-rose-500">*</span>
                  </label>
                  <textarea
                    rows={4}
                    value={pendingStatusChange.remarks}
                    onChange={(event) =>
                      setPendingStatusChange((prev) => (prev ? { ...prev, remarks: event.target.value } : prev))
                    }
                    className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm"
                    placeholder="Write the remarks before updating this status"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-4">
                <button
                  type="button"
                  onClick={closeStatusModal}
                  className="rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmStatusUpdate}
                  disabled={savingId === pendingStatusChange.row.id}
                  className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                >
                  {savingId === pendingStatusChange.row.id ? "Saving..." : "Save Status"}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </CompanyLayout>
  );
}
